/**
 * Block — wrapper de seção do Design System Bloco D (.block).
 *
 * Card com cabeçalho (título + ações opcionais) + corpo. É o agrupador
 * canônico usado em telas de detalhe (Pedido, Orçamento, Cliente, etc.)
 * e em qualquer seção que precise de um container com borda/elevação.
 *
 * Uso:
 *   <Block title="Cliente" actions={<button>Editar</button>}>
 *     ...conteúdo...
 *   </Block>
 *
 *   <Block title="Endereço" subtitle="Entrega" tone="info">...</Block>
 *
 *   <Block title="Anexos" collapsible defaultCollapsed>...</Block>
 *
 * Props:
 *   - title?: node — título do bloco (some o head se ausente + sem actions)
 *   - subtitle?: node — texto secundário ao lado/abaixo do título
 *   - actions?: node — botões/controles à direita do head
 *   - tone?: tone do getToneTokens (info/success/warning/danger/neutral) —
 *     aplica cor de borda/fundo sutil ao bloco. Default neutro (surface).
 *   - padded?: bool (default true) — padding interno no corpo
 *   - collapsible?: bool (default false) — o título do head vira um botão que
 *     abre/fecha o corpo (chevron + transição suave). `actions` continuam
 *     clicáveis independentemente (ficam fora do botão de toggle).
 *     Retrocompatível: blocos sem essa prop ficam idênticos.
 *   - defaultCollapsed?: bool (default false) — quando collapsible, define se
 *     o bloco nasce fechado.
 *   - children: node — corpo do bloco
 */
import React, { useId, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import {
  BlockRoot,
  BlockHead,
  BlockHeadCollapsible,
  BlockHeadToggle,
  BlockTitleWrap,
  BlockTitle,
  BlockSubtitle,
  BlockActions,
  BlockActionsCollapsible,
  BlockChevron,
  BlockBody,
  BlockCollapse,
} from "./styles";

export default function Block({
  title,
  subtitle,
  actions,
  tone = "default",
  padded = true,
  collapsible = false,
  defaultCollapsed = false,
  children,
  ...rest
}) {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);
  const bodyId = useId();

  const hasHead = title != null || actions != null;

  const titleWrap = (
    <BlockTitleWrap>
      {title != null && <BlockTitle>{title}</BlockTitle>}
      {subtitle != null && <BlockSubtitle>{subtitle}</BlockSubtitle>}
    </BlockTitleWrap>
  );

  const body = <BlockBody $padded={padded}>{children}</BlockBody>;

  return (
    <BlockRoot $tone={tone} {...rest}>
      {hasHead &&
        (collapsible ? (
          <BlockHeadCollapsible>
            <BlockHeadToggle
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-controls={bodyId}
            >
              {titleWrap}
              <BlockChevron $collapsed={collapsed} aria-hidden="true">
                <FiChevronDown size={18} />
              </BlockChevron>
            </BlockHeadToggle>
            {actions != null && (
              <BlockActionsCollapsible>{actions}</BlockActionsCollapsible>
            )}
          </BlockHeadCollapsible>
        ) : (
          <BlockHead>
            {titleWrap}
            {actions != null && <BlockActions>{actions}</BlockActions>}
          </BlockHead>
        ))}

      {collapsible ? (
        <BlockCollapse
          id={bodyId}
          $collapsed={collapsed}
          aria-hidden={collapsed}
        >
          {body}
        </BlockCollapse>
      ) : (
        body
      )}
    </BlockRoot>
  );
}
